import { useEffect, useMemo, useState } from 'react';
import { Coins, Bot, Workflow, TrendingUp, ShieldAlert, MessageSquare, Play, type LucideIcon } from 'lucide-react';
import { useAgentStudioNavigate } from '../lib/routes';
import { PageHeader } from '../components/PageHeader';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { BudgetBar } from '../components/BudgetBar';
import { CardSkeletonGrid, EmptyState } from '../components/ui/States';
import { agentsService } from '../services/agentsService';
import { flowsService } from '../services/flowsService';
import { budgetService } from '../services/budgetService';
import { usageService, type UsageSummary } from '../services/usageService';
import { formatUSD, formatRelativeTime, cn } from '../lib/utils';
import type { Agent, Flow, Area, Person, CostControl } from '../types';

interface CostRow {
  id: string;
  name: string;
  kind: 'agent' | 'flow';
  areaId?: string;
  personId?: string;
  cc: CostControl;
}

const POLICY_LABEL: Record<CostControl['onExceed'], { label: string; tone: 'neutral' | 'warning' | 'info' | 'danger' }> = {
  warn: { label: 'Avisar', tone: 'warning' },
  require_approval: { label: 'Exigir aprovação', tone: 'info' },
  block: { label: 'Bloquear', tone: 'danger' },
};

export function CostsPage() {
  const navigate = useAgentStudioNavigate();
  const [rows, setRows] = useState<CostRow[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [summary, setSummary] = useState<UsageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [agentNames, setAgentNames] = useState<Record<string, string>>({});
  const [flowNames, setFlowNames] = useState<Record<string, string>>({});

  useEffect(() => {
    Promise.all([agentsService.list(), flowsService.list(), budgetService.listAreas(), budgetService.listPeople(), usageService.summary()]).then(
      ([agents, flows, a, p, sum]) => {
        setSummary(sum);
        setAgentNames(Object.fromEntries(agents.map((ag: Agent) => [ag.id, ag.name])));
        setFlowNames(Object.fromEntries(flows.map((f: Flow) => [f.id, f.name])));
        // Teto "vazio" para entidades que têm gasto real mas não configuraram controle de custo.
        const ccZero: CostControl = { monthlyCapBRL: 0, perRunCapBRL: 0, spentBRL: 0, onExceed: 'warn' };
        const r: CostRow[] = [];
        const seen = new Set<string>();
        agents.forEach((ag: Agent) => {
          if (ag.costControl) { r.push({ id: ag.id, name: ag.name, kind: 'agent', areaId: ag.costControl.areaId, personId: ag.costControl.personId, cc: ag.costControl }); seen.add(ag.id); }
        });
        flows.forEach((f: Flow) => {
          if (f.costControl) { r.push({ id: f.id, name: f.name, kind: 'flow', areaId: f.costControl.areaId, personId: f.costControl.personId, cc: f.costControl }); seen.add(f.id); }
        });
        // Inclui agentes/fluxos que gastaram no mês mesmo sem teto configurado (senão somem da lista).
        Object.keys(sum.byAgent).forEach((id) => {
          if (seen.has(id)) return;
          const ag = agents.find((x: Agent) => x.id === id);
          r.push({ id, name: ag?.name ?? id, kind: 'agent', areaId: ag?.costControl?.areaId, personId: ag?.costControl?.personId, cc: ccZero });
          seen.add(id);
        });
        Object.keys(sum.byFlow).forEach((id) => {
          if (seen.has(id)) return;
          const f = flows.find((x: Flow) => x.id === id);
          r.push({ id, name: f?.name ?? id, kind: 'flow', areaId: f?.costControl?.areaId, personId: f?.costControl?.personId, cc: ccZero });
          seen.add(id);
        });
        setRows(r);
        setAreas(a);
        setPeople(p);
        setLoading(false);
      },
    );
  }, []);

  const agentNameById = (id: string) => agentNames[id] ?? id;

  // Gasto real do mês (ledger) por linha: agentes em byAgent, fluxos em byFlow.
  const spentOf = (r: CostRow) =>
    (r.kind === 'agent' ? summary?.byAgent[r.id]?.costUsd : summary?.byFlow[r.id]?.costUsd) ?? 0;

  const totals = useMemo(() => {
    const spent = summary?.totals.costUsd ?? 0;
    const cap = rows.reduce((acc, r) => acc + r.cc.monthlyCapBRL, 0);
    const over = rows.filter((r) => r.cc.monthlyCapBRL > 0 && spentOf(r) > r.cc.monthlyCapBRL).length;
    return { spent, cap, over };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, summary]);

  const areaName = (id?: string) => areas.find((a) => a.id === id)?.name ?? '—';
  const personName = (id?: string) => people.find((p) => p.id === id)?.name ?? '—';

  const byArea = useMemo(() => {
    return areas
      .map((area) => {
        const items = rows.filter((r) => r.areaId === area.id);
        const spent = summary?.byArea[area.id]?.costUsd ?? 0;
        return { area, spent, count: items.length };
      })
      .filter((x) => x.count > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areas, rows, summary]);

  return (
    <>
      <PageHeader
        title="Custos"
        subtitle="Gasto e tetos de cada agente e fluxo no período"
        actions={
          <button onClick={() => navigate('/budget')} className="rounded-xl border border-line px-3 py-2 text-sm font-medium text-ink hover:bg-cloud">
            Gerenciar áreas & budget
          </button>
        }
      />
      <div className="flex-1 overflow-y-auto scrollbar-thin p-8">
        {loading ? (
          <CardSkeletonGrid count={3} />
        ) : rows.length === 0 ? (
          <EmptyState icon={Coins} title="Nenhum custo configurado" description="Defina tetos de custo nos agentes (aba Guardrails) e nos fluxos (botão Custo)." />
        ) : (
          <div className="space-y-6">
            {/* KPIs */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <Metric icon={Coins} label="Gasto no mês" value={formatUSD(totals.spent)} accent="#E11019" />
              <Metric icon={TrendingUp} label="Teto mensal configurado" value={formatUSD(totals.cap)} accent="#0E2A47" />
              <Metric icon={TrendingUp} label="Uso do teto" value={totals.cap > 0 ? `${Math.round((totals.spent / totals.cap) * 100)}%` : '—'} accent="#1FAA6B" />
              <Metric icon={ShieldAlert} label="Itens acima do teto" value={String(totals.over)} accent={totals.over ? '#E04646' : '#5B6B7B'} />
            </div>

            {/* Por área */}
            {byArea.length > 0 && (
              <div>
                <h2 className="mb-3 text-sm font-semibold text-ink">Por área</h2>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                  {byArea.map(({ area, spent }) => (
                    <Card key={area.id} className="p-4">
                      <p className="text-sm font-semibold text-ink">{area.name}</p>
                      <div className="mt-3">
                        <BudgetBar used={spent} total={area.budgetBRL} />
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Por conversa (sobrevive à exclusão da conversa) */}
            {summary && summary.byConversation.length > 0 && (
              <Card>
                <div className="flex items-center justify-between border-b border-line px-5 py-3.5">
                  <h2 className="text-base font-semibold text-ink">Por conversa (chat)</h2>
                  <span className="text-xs text-slate">o custo permanece mesmo se a conversa for excluída</span>
                </div>
                <div className="divide-y divide-line">
                  {summary.byConversation.slice(0, 12).map((c) => (
                    <div key={c.conversationId} className="flex items-center gap-4 px-5 py-3">
                      <MessageSquare className="h-4 w-4 shrink-0 text-slate" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-ink">{c.title}</p>
                        <p className="text-xs text-slate">{agentNameById(c.agentId)} · {c.calls} chamada(s) · {c.tokens.toLocaleString('pt-BR')} tokens</p>
                      </div>
                      <span className="text-sm font-semibold text-ink">{formatUSD(c.costUsd)}</span>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Por execução de fluxo (cada run listado separadamente) */}
            {summary && summary.byRun.length > 0 && (
              <Card>
                <div className="flex items-center justify-between border-b border-line px-5 py-3.5">
                  <h2 className="text-base font-semibold text-ink">Por execução de fluxo</h2>
                  <span className="text-xs text-slate">cada execução com seu custo real</span>
                </div>
                <div className="divide-y divide-line">
                  {summary.byRun.slice(0, 20).map((r) => (
                    <button
                      key={r.runId}
                      onClick={() => navigate(`/runs/${r.runId}`)}
                      className="flex w-full items-center gap-4 px-5 py-3 text-left transition-colors hover:bg-cloud"
                    >
                      <Play className="h-4 w-4 shrink-0 text-navy" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-ink">{r.flowId ? flowNames[r.flowId] ?? r.flowId : 'Fluxo'}</p>
                        <p className="text-xs text-slate">{formatRelativeTime(r.at)} · {r.calls} chamada(s) · {r.tokens.toLocaleString('pt-BR')} tokens</p>
                      </div>
                      <span className="text-sm font-semibold text-ink">{formatUSD(r.costUsd)}</span>
                    </button>
                  ))}
                </div>
              </Card>
            )}

            {/* Tabela de agentes & fluxos */}
            <Card>
              <div className="border-b border-line px-5 py-3.5">
                <h2 className="text-base font-semibold text-ink">Agentes & Fluxos</h2>
              </div>
              <div className="divide-y divide-line">
                {rows.map((r) => {
                  const spent = spentOf(r);
                  const over = r.cc.monthlyCapBRL > 0 && spent > r.cc.monthlyCapBRL;
                  const near = !over && r.cc.monthlyCapBRL > 0 && spent > r.cc.monthlyCapBRL * 0.85;
                  const policy = POLICY_LABEL[r.cc.onExceed];
                  return (
                    <button
                      key={r.id}
                      onClick={() => navigate(r.kind === 'agent' ? `/agents/${r.id}` : `/flows/${r.id}`)}
                      className="flex w-full flex-wrap items-center gap-4 px-5 py-3.5 text-left transition-colors hover:bg-cloud"
                    >
                      <div className={cn('flex h-9 w-9 items-center justify-center rounded-lg text-white', r.kind === 'agent' ? 'bg-brand-orange' : 'bg-navy')}>
                        {r.kind === 'agent' ? <Bot className="h-4 w-4" /> : <Workflow className="h-4 w-4" />}
                      </div>
                      <div className="min-w-[160px] flex-1">
                        <p className="truncate text-sm font-medium text-ink">{r.name}</p>
                        <p className="text-xs text-slate">{areaName(r.areaId)} · {personName(r.personId)}</p>
                      </div>
                      <div className="w-44">
                        <BudgetBar used={spent} total={r.cc.monthlyCapBRL || spent || 1} />
                      </div>
                      <div className="flex w-28 flex-col items-start text-xs text-slate">
                        <span>por exec.: <strong className="text-ink">{r.cc.perRunCapBRL ? formatUSD(r.cc.perRunCapBRL) : '—'}</strong></span>
                        <Badge tone={policy.tone}>{policy.label}</Badge>
                      </div>
                      <div className="w-24 text-right">
                        {over ? <Badge tone="danger" dot>Acima</Badge> : near ? <Badge tone="warning" dot>No limite</Badge> : r.cc.monthlyCapBRL ? <Badge tone="success" dot>Ok</Badge> : <Badge tone="neutral">Sem teto</Badge>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </Card>
          </div>
        )}
      </div>
    </>
  );
}

function Metric({ icon: Icon, label, value, accent }: { icon: LucideIcon; label: string; value: string; accent: string }) {
  return (
    <Card className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-slate">{label}</p>
          <p className="mt-1 text-2xl font-semibold text-ink">{value}</p>
        </div>
        <div className="flex h-10 w-10 items-center justify-center rounded-xl" style={{ backgroundColor: `${accent}1a` }}>
          <Icon className="h-5 w-5" style={{ color: accent }} />
        </div>
      </div>
    </Card>
  );
}
